import { DEFAULT_ROOM } from '@xeetr/types/defaults'
import { ViewRoom } from '@xeetr/types/models'
import { copyRoom } from '@xeetr/types/utils'
import { useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import AddObjectButton from '../../components/settings/common/AddObjectButton'
import RoomForm from '../../components/settings/rooms/RoomForm'
import RoomHeader from '../../components/settings/rooms/RoomHeader'
import RoomsFooter from '../../components/settings/rooms/RoomsFooter'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import Loading from '@/components/loading/Loading'
import { useConfirmDialog } from '@/hooks/common/useConfirmDialog'
import { useRooms } from '@/hooks/useRooms'
import { useRoomsMutation } from '@/hooks/useRoomsMutation'
import { DirtyFormsMap, FormControlHandle } from '@/types/RoomForm'
import { useTenant } from '@/hooks'
import LabeledInputSwitch from '@/components/inputs/LabeledInputSwitch'

const Rooms = () => {
  const { rooms, isLoading } = useRooms()
  const { tenant, updateTenantSettings } = useTenant()
  const { createRoom, updateRoom, deleteRoom } = useRoomsMutation()
  const [expandedItems, setExpandedItems] = useState<string[]>([])
  const [activeFormControl, setActiveFormControl] = useState<FormControlHandle | null>(null)
  const [dirtyForms, setDirtyForms] = useState<DirtyFormsMap>(new Map())
  const confirmDialog = useConfirmDialog()

  const handleCreate = () => {
    const newRoom: ViewRoom = {
      ...DEFAULT_ROOM,
      id: uuidv4(),
    }
    createRoom(newRoom)
    // Expand the new room after creation
    setTimeout(() => setExpandedItems([newRoom.id]), 100)
  }

  const handleCopy = (room: ViewRoom) => {
    const copiedRoom: ViewRoom = {
      ...copyRoom(room),
      id: uuidv4(),
      tables: copyRoom(room).tables.map((table) => ({
        ...table,
        id: uuidv4(),
      })),
    }
    createRoom(copiedRoom)
  }

  const handleDelete = (id: string) => {
    confirmDialog.confirm(
      {
        title: 'Löschen bestätigen!',
        description: 'Möchten Sie den Raum wirklich löschen?',
        confirmText: 'Löschen',
        cancelText: 'Abbrechen',
        variant: 'destructive',
      },
      () => {
        deleteRoom(id)
        // Remove from expanded if was open
        setExpandedItems((prev) => prev.filter((item) => item !== id))
      }
    )
  }

  const handleSwap = (fromIndex: number, toIndex: number) => {
    // Update order for both rooms
    const roomsCopy = [...rooms]
    const fromRoom = roomsCopy[fromIndex]
    const toRoom = roomsCopy[toIndex]

    updateRoom({
      id: fromRoom.id,
      room: { order: toRoom.order },
    })
    updateRoom({
      id: toRoom.id,
      room: { order: fromRoom.order },
    })
  }

  /**
   * Handle accordion value change with dirty form warning
   * If user tries to close a dirty form, show confirmation dialog
   */
  const handleAccordionChange = (newExpandedItems: string[]) => {
    const closingItems = expandedItems.filter((item) => !newExpandedItems.includes(item))

    // Check if any closing item has dirty form
    const dirtyClosingItem = closingItems.find((item) => dirtyForms.get(item))

    if (dirtyClosingItem) {
      // Show warning dialog
      confirmDialog.confirm(
        {
          title: 'Nicht gespeicherte Änderungen',
          description:
            'Sie haben nicht gespeicherte Änderungen. Möchten Sie diese verwerfen und fortfahren?',
          confirmText: 'Verwerfen',
          cancelText: 'Abbrechen',
          variant: 'destructive',
        },
        () => {
          // User confirmed, allow close and discard changes
          setExpandedItems(newExpandedItems)
          setDirtyForms((prev) => {
            const next = new Map(prev)
            next.delete(dirtyClosingItem)
            return next
          })
        }
      )
    } else {
      // No dirty forms, allow close
      setExpandedItems(newExpandedItems)
    }
  }

  /**
   * Register/unregister form control from child RoomForm
   * Updates dirty state tracking
   */
  const handleRegisterForm = (handle: FormControlHandle | null) => {
    setActiveFormControl(handle)

    // Update dirty state map
    if (handle) {
      setDirtyForms((prev) => {
        const next = new Map(prev)
        next.set(handle.roomId, handle.isDirty)
        return next
      })
    }
  }

  return (
    <div className="flex flex-col">
      <div className="mb-6">
        <h2 className="text-lg font-semibold">Räume</h2>
        <p className="text-muted-foreground text-sm">
          Verwalten Sie die Räume Ihres Restaurants, um verschiedene Bereiche zu organisieren.
        </p>
      </div>
      <div className="md:flex md:justify-between">
        <LabeledInputSwitch
          className="col-span-1 py-3 md:col-span-6"
          checked={tenant?.anyroom ?? false}
          label="Buchungen ohne Raumzuweisung erlauben"
          onValueChange={(checked) => updateTenantSettings('anyroom', checked)}
        />
        <AddObjectButton append={handleCreate} label="Neuer Raum" />
      </div>
      <Loading loading={isLoading} label="Räume werden geladen...">
        <Accordion
          type="multiple"
          className="mt-4"
          value={expandedItems}
          onValueChange={handleAccordionChange}
        >
          {rooms.map((room, roomIndex) => (
            <AccordionItem key={room.id} value={room.id}>
              <AccordionTrigger>
                <RoomHeader room={room} roomIndex={roomIndex} rooms={rooms} swapRoom={handleSwap} />
              </AccordionTrigger>
              <AccordionContent>
                <RoomForm
                  room={room}
                  isExpanded={expandedItems.includes(room.id)}
                  onRegisterForm={handleRegisterForm}
                  onCopy={() => handleCopy(room)}
                  onDelete={() => handleDelete(room.id)}
                  onUpdate={(updatedRoom) => updateRoom({ id: room.id, room: updatedRoom })}
                />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Loading>
      <ConfirmDialog
        open={confirmDialog.isOpen}
        onOpenChange={confirmDialog.setIsOpen}
        title={confirmDialog.options.title}
        description={confirmDialog.options.description}
        confirmText={confirmDialog.options.confirmText}
        cancelText={confirmDialog.options.cancelText}
        variant={confirmDialog.options.variant}
        onConfirm={confirmDialog.handleConfirm}
      />
      <RoomsFooter formControl={activeFormControl} />
    </div>
  )
}

export default Rooms
